import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { 
  Home, 
  Calendar, 
  Users, 
  AlertTriangle,
  TrendingUp,
  Clock,
  ArrowRight,
  Loader2
} from "lucide-react";
import { getDashboardStats, seedData } from "@/lib/api";

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await getDashboardStats();
      setStats(res.data);
    } catch (error) {
      console.error("Error loading stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await seedData();
      toast.success("Données de base créées avec succès !");
      loadStats();
    } catch (error) {
      console.error("Error seeding:", error);
      toast.error("Erreur lors de la création des données");
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#004333]" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-[#004333]/10 flex items-center justify-center">
              <Home className="w-6 h-6 text-[#004333]" />
            </div>
            <span className="text-sm text-gray-500">Actifs</span>
          </div>
          <div className="font-mono text-3xl font-bold text-[#004333]">
            {stats?.total_logements || 0}
          </div>
          <div className="text-sm text-gray-500">Logements</div>
        </div>
        
        <div className="bg-white p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-[#C9922A]/10 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-[#C9922A]" />
            </div>
            <span className="badge-secondary">{stats?.reservations_en_attente || 0} en attente</span>
          </div>
          <div className="font-mono text-3xl font-bold text-[#004333]">
            {stats?.total_reservations || 0}
          </div>
          <div className="text-sm text-gray-500">Réservations</div>
        </div>
        
        <div className="bg-white p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-sm text-green-600">+12%</span>
          </div>
          <div className="font-mono text-3xl font-bold text-[#004333]">
            {stats?.total_revenus?.toLocaleString() || 0} €
          </div>
          <div className="text-sm text-gray-500">Revenus totaux</div>
        </div>
        
        <div className="bg-white p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <span className="badge-primary">{stats?.total_leads || 0} nouveaux</span>
          </div>
          <div className="font-mono text-3xl font-bold text-[#004333]">
            {stats?.total_leads || 0}
          </div>
          <div className="text-sm text-gray-500">Leads entrants</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Prochaines arrivées */}
        <div className="lg:col-span-2 bg-white border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-display text-xl text-[#004333]">
              Prochaines arrivées
            </h2>
            <Link to="/admin/reservations" className="text-sm text-[#C9922A] hover:underline flex items-center gap-1">
              Voir tout <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="p-6">
            {stats?.prochaines_arrivees && stats.prochaines_arrivees.length > 0 ? (
              <div className="space-y-4">
                {stats.prochaines_arrivees.slice(0, 5).map((reservation) => (
                  <div key={reservation.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                    <div>
                      <div className="font-semibold text-[#004333]">{reservation.voyageur_nom}</div>
                      <div className="text-sm text-gray-500">
                        Arrivée le {reservation.date_arrivee}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold text-[#004333]">{reservation.montant_total} €</div>
                      <div className="text-sm text-gray-500">{reservation.nb_personnes} pers.</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Aucune arrivée prévue cette semaine</p>
              </div>
            )}
          </div>
        </div>

        {/* Alertes et actions rapides */}
        <div className="space-y-6">
          {/* Alertes */}
          <div className="bg-white border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="font-display text-xl text-[#004333]">Alertes</h2>
            </div>
            <div className="p-6">
              {stats?.incidents_ouverts > 0 ? (
                <Link to="/admin/incidents" className="flex items-center gap-4 p-4 bg-red-50 hover:bg-red-100 transition-colors">
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                  <div>
                    <div className="font-semibold text-red-700">
                      {stats.incidents_ouverts} incident{stats.incidents_ouverts > 1 ? "s" : ""} ouvert{stats.incidents_ouverts > 1 ? "s" : ""}
                    </div>
                    <div className="text-sm text-red-500">À traiter</div>
                  </div>
                </Link>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <span className="text-green-500 font-medium">Tout est en ordre !</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions rapides */}
          <div className="bg-white border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="font-display text-xl text-[#004333]">Actions rapides</h2>
            </div>
            <div className="p-6 space-y-3">
              <Link to="/admin/logements" className="block w-full btn-secondary text-center">
                Ajouter un logement
              </Link>
              <Link to="/admin/reservations" className="block w-full btn-secondary text-center">
                Voir les réservations
              </Link>
              <Link to="/admin/rapports" className="block w-full btn-secondary text-center">
                Générer un rapport
              </Link>
              <button
                onClick={handleSeed}
                disabled={seeding}
                className="w-full btn-primary"
                data-testid="seed-data-btn"
              >
                {seeding ? "Création..." : "Initialiser les données de base"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
